<!-- Decorative: the form beside this panel carries the page's meaning, so the
     mosaic is hidden from assistive tech. Coordinates are Figma's, in the
     960x1048 design box — "slice" is the SVG spelling of object-fit: cover.
     What the two cards say is the real documented API, not the mockup's
     invented one — see auth-panel.ts's header. -->
<svg
  class="auth-panel"
  viewBox="0 0 960 1048"
  preserveAspectRatio="xMidYMid slice"
  aria-hidden="true"
  focusable="false"
>
  <defs>
    <linearGradient id="authPanelGutter" x1="0" y1="0" x2="960" y2="1048" gradientUnits="userSpaceOnUse">
      <stop offset="0" stop-color="#9CA5C5" />
      <stop offset="1" stop-color="#C5D0F8" />
    </linearGradient>

    <!-- One code sheet for the whole mosaic, clipped to the tiles, so a
         listing reads as continuous across the gutters between them. -->
    <clipPath id="authPanelTiles">
      <rect
        *ngFor="let t of codeTiles; trackBy: trackByIndex"
        [attr.x]="t.x"
        [attr.y]="t.y"
        [attr.width]="tileW"
        [attr.height]="tileH"
        rx="12"
      />
    </clipPath>

    <filter id="authPanelCardShadow" x="-25%" y="-25%" width="150%" height="150%">
      <feDropShadow dx="0" dy="10" stdDeviation="14" flood-color="#0A1B45" flood-opacity="0.16" />
    </filter>
  </defs>

  <rect x="0" y="0" width="960" height="1048" fill="url(#authPanelGutter)" />

  <!-- ── Tiles + the code showing through them ─────────────────────
       Luminosity blending is carried over from the Figma group: it lets the
       gutter's gradient tint the sheet instead of sitting behind flat white. -->
  <g class="auth-panel__mosaic">
    <rect
      *ngFor="let t of codeTiles; trackBy: trackByIndex"
      class="auth-panel__tile"
      [attr.x]="t.x"
      [attr.y]="t.y"
      [attr.width]="tileW"
      [attr.height]="tileH"
      rx="12"
    />

    <g clip-path="url(#authPanelTiles)">
      <text
        *ngFor="let col of codeColumns; trackBy: trackByIndex"
        class="auth-panel__code"
        [attr.x]="col.x"
        [attr.y]="col.y"
        [attr.opacity]="col.opacity"
        xml:space="preserve"
      ><tspan *ngFor="let line of col.lines; let i = index" [attr.x]="col.x" [attr.dy]="i ? 20 : 0">{{ line || ' ' }}</tspan></text>
    </g>
  </g>

  <!-- ── Brand shape ─────────────────────────────────────────────── -->
  <g transform="translate(148 0)">
    <path class="auth-panel__brand" [attr.d]="brandPath" />
  </g>

  <!-- Wordmark: 240 wide, centred in the blue tile. -->
  <g transform="translate(188 275.536) scale(0.4545455)">
    <path
      *ngFor="let p of logoPaths; trackBy: trackByIndex"
      [class]="p.dot ? 'auth-panel__logo-dot' : 'auth-panel__logo-mark'"
      [attr.d]="p.d"
      [attr.fill-rule]="p.evenodd ? 'evenodd' : null"
      [attr.clip-rule]="p.evenodd ? 'evenodd' : null"
    />
  </g>

  <text class="auth-panel__tagline" x="562" y="705.24">Enterprise Web<tspan x="562" dy="32">Intelligence API</tspan></text>
  <text class="auth-panel__byline" x="562" y="763.62">-mila. is a product of CIKISI S.A.</text>
  <text class="auth-panel__link" x="562" y="787.62">www.cikisi.com</text>

  <!-- ── Card: POST /api/rag ─────────────────────────────────────── -->
  <g>
    <rect
      class="auth-panel__card"
      x="492"
      y="112"
      [attr.width]="tileW"
      [attr.height]="tileH"
      rx="12"
      filter="url(#authPanelCardShadow)"
    />

    <rect class="auth-panel__badge auth-panel__badge--post" x="512" y="132" width="34" height="18" rx="4" />
    <text class="auth-panel__badge-text auth-panel__badge-text--post" x="518" y="144.375">{{ rag.method }}</text>
    <text class="auth-panel__path" x="554" y="145.125">{{ rag.path }}</text>
    <text class="auth-panel__meta" x="792" y="144.465" text-anchor="end">{{ snippetRows.length }} snippets · {{ rag.credit }} credit</text>

    <rect class="auth-panel__rule" x="512" y="162" width="280" height="1" />
    <text class="auth-panel__label" x="512" y="188">SNIPPETS</text>

    <!-- One row per snippet the endpoint's own sample response returns:
         its article title, the passage itself, its relevance score and the
         domain it was published on. -->
    <g *ngFor="let row of snippetRows; trackBy: trackByIndex">
      <text class="auth-panel__rank" x="512" [attr.y]="row.y">{{ row.rank }}</text>

      <text class="auth-panel__title" x="536" [attr.y]="row.y"><tspan
        *ngFor="let line of row.title; let i = index" x="536" [attr.dy]="i ? 14 : 0">{{ line }}</tspan></text>

      <text class="auth-panel__excerpt" x="536" [attr.y]="row.y + 34"><tspan
        *ngFor="let line of row.excerpt; let i = index" x="536" [attr.dy]="i ? 13 : 0">{{ line }}</tspan></text>

      <rect class="auth-panel__bar-track" x="536" [attr.y]="row.y + 72" width="34" height="4" rx="2" />
      <rect class="auth-panel__bar-fill" x="536" [attr.y]="row.y + 72" [attr.width]="row.bar" height="4" rx="2" />
      <text class="auth-panel__meta" x="578" [attr.y]="row.y + 79">{{ row.score }}</text>
      <text class="auth-panel__source" x="792" [attr.y]="row.y + 79" text-anchor="end">{{ row.domain }}</text>
    </g>

    <g *ngFor="let chip of ragChips; trackBy: trackByIndex">
      <rect class="auth-panel__chip" [attr.x]="chip.x" y="472" [attr.width]="chip.w" height="20" rx="10" />
      <text class="auth-panel__chip-text" [attr.x]="chip.x + 8" y="485.465">{{ chip.label }}</text>
    </g>
  </g>

  <!-- ── Card: POST /api/answer ──────────────────────────────────── -->
  <g>
    <rect
      class="auth-panel__card"
      x="148"
      y="536"
      [attr.width]="tileW"
      [attr.height]="tileH"
      rx="12"
      filter="url(#authPanelCardShadow)"
    />

    <rect class="auth-panel__badge auth-panel__badge--post" x="168" y="557" width="34" height="18" rx="4" />
    <text class="auth-panel__badge-text auth-panel__badge-text--post" x="174" y="569.375">{{ answer.method }}</text>
    <text class="auth-panel__path" x="210" y="570.125">{{ answer.path }}</text>
    <rect class="auth-panel__chip" x="403" y="556" width="45" height="20" rx="4" />
    <text class="auth-panel__chip-text" x="411" y="569.465">{{ answerMeta.mode }}</text>

    <rect class="auth-panel__rule" x="168" y="588" width="280" height="1" />
    <text class="auth-panel__label" x="168" y="630.965">RESPONSE</text>

    <text class="auth-panel__json" x="168" y="657.438" xml:space="preserve"><tspan *ngFor="let line of answerJson; let i = index" x="168" [attr.dy]="i ? 20 : 0"><tspan *ngFor="let span of line; trackBy: trackByIndex" [attr.class]="span.c ? 'auth-panel__json--' + span.c : null">{{ span.t }}</tspan></tspan></text>

    <g *ngFor="let chip of answerChips; trackBy: trackByIndex">
      <rect class="auth-panel__chip" [attr.x]="chip.x" y="896" [attr.width]="chip.w" height="20" rx="10" />
      <text class="auth-panel__chip-text" [attr.x]="chip.x + 8" y="909.465">{{ chip.label }}</text>
    </g>
  </g>
</svg>

<!-- Phones (see _auth.scss): the mosaic doesn't fit above a form, so the
     panel is just the wordmark instead, in the app's own blue. -->
<div class="auth-panel-mobile" aria-hidden="true">
  <svg class="auth-panel-mobile__logo" viewBox="0 0 528 160.442" focusable="false">
    <path
      *ngFor="let p of logoPaths; trackBy: trackByIndex"
      [class]="p.dot ? 'auth-panel__logo-dot' : 'auth-panel__logo-mark'"
      [attr.d]="p.d"
      [attr.fill-rule]="p.evenodd ? 'evenodd' : null"
      [attr.clip-rule]="p.evenodd ? 'evenodd' : null"
    />
  </svg>
</div>
